import * as React from "react";
import type { PresetListProps, SinglePreset, RangePreset } from "../types";
import "./PresetList.css";

// =============================================================================
// PresetList Component
// Scrollable list of quick selection presets
// =============================================================================

export function PresetList<T extends SinglePreset | RangePreset>({
  presets,
  activeValue,
  onPresetClick,
  className,
}: PresetListProps<T>) {
  const listRef = React.useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = React.useState(false);
  const [canScrollDown, setCanScrollDown] = React.useState(false);

  // Return null for empty presets
  if (!presets || presets.length === 0) {
    return null;
  }

  // ---------------------------------------------------------------------------
  // Scroll State Management
  // ---------------------------------------------------------------------------

  const updateScrollState = React.useCallback(() => {
    const el = listRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    setCanScrollUp(scrollTop > 0);
    setCanScrollDown(scrollTop + clientHeight < scrollHeight - 1);
  }, []);

  // Check scroll state on mount and when presets change
  React.useEffect(() => {
    updateScrollState();
    // Also check after a brief delay to handle dynamic content
    const timer = setTimeout(updateScrollState, 50);
    return () => clearTimeout(timer);
  }, [presets, updateScrollState]);

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  const handleScroll = React.useCallback(() => {
    updateScrollState();
  }, [updateScrollState]);

  const handleClick = React.useCallback(
    (preset: T) => {
      onPresetClick(preset);
    },
    [onPresetClick]
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent, preset: T) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onPresetClick(preset);
      }
    },
    [onPresetClick]
  );

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const isPresetActive = (preset: T): boolean => {
    if (activeValue === undefined) return false;

    const presetValue = preset.value;

    // Single preset
    if (typeof presetValue === "string") {
      return presetValue === activeValue;
    }

    // Range preset (tuple comparison)
    if (Array.isArray(presetValue) && Array.isArray(activeValue)) {
      return presetValue[0] === activeValue[0] && presetValue[1] === activeValue[1];
    }

    return false;
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const wrapperClasses = [
    "preset-list-wrapper",
    canScrollUp && "preset-list-wrapper--can-scroll-up",
    canScrollDown && "preset-list-wrapper--can-scroll-down",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapperClasses}>
      <div
        ref={listRef}
        className="preset-list"
        role="list"
        onScroll={handleScroll}
      >
        {presets.map((preset, index) => {
          const isActive = isPresetActive(preset);

          return (
            <div
              key={index}
              className={`preset-list__item ${isActive ? "preset-list__item--active" : ""}`}
              role="listitem"
              tabIndex={0}
              onClick={() => handleClick(preset)}
              onKeyDown={(e) => handleKeyDown(e, preset)}
              aria-current={isActive ? "true" : undefined}
            >
              <span className="preset-list__label">{preset.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
